<template>
  <div class="mb-6">
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full mb-4 mt-1 gap-2"
    >
      <p class="font-semibold text-base">{{ title }}</p>

      <div class="flex gap-2">
        <v-menu v-model="isCreateMenuOpen" :close-on-content-click="false" location="bottom end">
          <template v-slot:activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="flex items-center gap-1 font-semibold text-primary30 rounded px-3 py-1 text-sm primary30-btn"
            >
              {{ itemLabel }}
              <v-icon color="primary" :icon="mdiPlus" size="24" />
            </button>
          </template>

          <v-card min-width="400">
            <v-card-text>
              <v-row align="center">
                <v-col>
                  <v-autocomplete
                    v-model="newItemName"
                    v-model:search="searchText"
                    :items="options"
                    :loading="isLoading"
                    item-title="name"
                    item-value="name"
                    :label="itemLabel"
                    variant="outlined"
                    density="compact"
                    hide-details
                    autofocus
                    @keydown.enter="handleCreate"
                  >
                    <template #no-data>
                      <v-list-item v-if="searchText" @click="selectNewItem">
                        <v-list-item-title>
                          Adicionar "<span class="font-weight-bold">{{ searchText }}</span
                          >"
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="auto">
                  <FilledButton
                    :text="createButtonLabel || 'Adicionar'"
                    color="primary30"
                    height="40px"
                    width="120px"
                    :disabled="!newItemName || String(newItemName).trim() === ''"
                    @click="handleCreate"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-menu>

        <v-menu>
          <template v-slot:activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="flex items-center font-semibold gap-1 text-primary30 rounded px-3 py-1 text-sm primary30-btn"
              :disabled="inactiveItems.length === 0"
              :class="{ 'opacity-50 cursor-not-allowed': inactiveItems.length === 0 }"
            >
              {{ itemActiveLabel }}
              <v-icon color="primary" :icon="mdiChevronDown" size="24" />
            </button>
          </template>

          <v-list>
            <v-list-item
              v-for="(item, index) in inactiveItems"
              :key="index"
              @click="$emit('reactivate', index)"
              class="cursor-pointer"
            >
              <v-list-item-title>{{ item }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
      <div v-for="(item, index) in items" :key="index" class="w-full max-w-[480px]">
        <EditableItemMenu
          :modelValue="item"
          @update:modelValue="(val) => $emit('update', index, val)"
          @edit="() => $emit('edit', index)"
          @rename="(val) => $emit('update', index, val)"
          @deactivate="() => $emit('deactivate', index)"
        />
      </div>
    </div>

    <div class="mt-4" v-if="inactiveItems.length > 0">
      <p class="mb-2 text-left text-sm font-semibold text-[#68688D]">{{ inactiveTitle }}</p>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="(item, index) in inactiveItems"
          :key="index"
          class="bg-gray-200 px-3 py-1 rounded text-sm flex items-center gap-2"
        >
          {{ item }}
        </span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { mdiPlus, mdiChevronDown } from '@mdi/js'
import EditableItemMenu from './EditableItemMenu.vue'
import {
  VMenu,
  VList,
  VListItem,
  VListItemTitle,
  VCard,
  VCardText,
  VAutocomplete,
  VIcon,
  VRow
} from 'vuetify/components'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import { useErrorToast } from '@/composables/useErrorToast'

const isCreateMenuOpen = ref(false)
const newItemName = ref('')
const options = ref<{ id: number; name: string }[]>([])
const service = new FieldOptionsService()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const searchText = ref('')

const emit = defineEmits<{
  (e: 'update', index: number, value: string): void
  (e: 'edit', index: number): void
  (e: 'deactivate', index: number): void
  (e: 'reactivate', index: number): void
  (e: 'create', value: string): void
}>()

const props = defineProps<{
  title: string
  itemLabel?: string
  itemActiveLabel?: string
  items: string[]
  inactiveItems: string[]
  createButtonLabel?: string
  type: 'positions' | 'shifts' | 'formations'
}>()

// "Turnos" é masculino; posições e formações, femininos.
const inactiveTitle = computed(() =>
  props.type === 'shifts' ? `${props.title} inativos` : `${props.title} inativas`
)

async function fetchOptions() {
  if (options.value.length > 0) return

  isLoading.value = true
  try {
    switch (props.type) {
      case 'positions':
        options.value = await service.getPositions()
        break
      case 'shifts':
        options.value = await service.getShifts()
        break
    }
  } catch (error) {
    // Carga disparada ao abrir o menu: a lista fica vazia e a próxima abertura tenta de novo.
    notifyError(error, 'Não foi possível carregar as opções.')
  } finally {
    isLoading.value = false
  }
}

watch(isCreateMenuOpen, (isOpen) => {
  if (isOpen) {
    fetchOptions()
  }
})

function handleCreate() {
  if (!newItemName.value || newItemName.value.trim() === '') {
    return
  }

  emit('create', newItemName.value.trim())

  newItemName.value = ''
  searchText.value = ''
  isCreateMenuOpen.value = false
}

function selectNewItem() {
  newItemName.value = searchText.value
  handleCreate()
}
</script>

<style scoped>
.primary30-btn {
  border: 1px solid #5abed3;
  color: #5abed3;
}
</style>
